import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Modal, Container, Header } from '@amzn/awsui-components-console';
import { cleanMermaidContent } from '../../utils/cleanMermaidContent';
import { sanitizeSvg } from '../../utils/sanitizeSvg';

export interface MermaidDiagramProps {
  content: string;
  title?: string;
}

// Internal Mermaid renderer using dynamic import
const MermaidRenderer: React.FC<{ content: string }> = ({ content }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const renderMermaid = async () => {
      try {
        // Dynamically import mermaid
        const mermaid = (await import('mermaid')).default;
        
        // Initialize mermaid
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'strict',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis'
          }
        });

        // Generate unique ID
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        
        // Render the diagram
        const { svg: renderedSvg } = await mermaid.render(id, content);
        setSvg(renderedSvg);
        setError('');
      } catch (err: any) {
        console.error('Error rendering mermaid diagram:', err);
        setError(err.message || 'Failed to render diagram');
        setSvg('');
      }
    };

    if (content) {
      renderMermaid();
    }
  }, [content]);

  if (error) {
    return (
      <Box color="text-status-error" padding="m">
        <div>Error rendering diagram: {error}</div>
        <pre style={{ fontSize: '12px', marginTop: '10px', whiteSpace: 'pre-wrap' }}>{content}</pre>
      </Box>
    );
  }

  if (!svg) {
    return <Box textAlign="center" padding="l">Loading diagram...</Box>;
  }

  return (
    <div 
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    />
  );
};

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ content, title = 'Data Flow Diagram' }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const cleanedContent = cleanMermaidContent(content);

  const handleDownload = () => {
    const blob = new Blob([cleanedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.mmd';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!cleanedContent) {
    return (
      <Container header={<Header variant="h3">{title}</Header>}>
        <Box padding="m" color="text-status-inactive">
          No diagram content available.
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Container
        header={
          <Header
            variant="h3"
            actions={
              <div style={{ display: 'flex', gap: '4px' }}>
                <Button
                  iconName="download"
                  variant="icon"
                  onClick={handleDownload}
                  ariaLabel="Download mermaid source"
                />
                <Button
                  iconName="expand"
                  variant="icon"
                  onClick={() => setIsFullscreen(true)}
                  ariaLabel="View diagram fullscreen"
                />
              </div>
            }
          >
            {title}
          </Header>
        }
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          overflow: 'auto', 
          maxHeight: '400px',
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          padding: '12px'
        }}>
          <MermaidRenderer content={cleanedContent} />
        </div>
      </Container>

      <Modal
        visible={isFullscreen}
        onDismiss={() => setIsFullscreen(false)}
        closeAriaLabel="Close fullscreen view"
        size="max"
        header={title}
      >
        <div
          style={{
            width: '100%',
            height: '70vh',
            overflow: 'auto',
            backgroundColor: '#ffffff',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MermaidRenderer content={cleanedContent} />
        </div>
      </Modal>
    </>
  );
};

export default MermaidDiagram;
